// Framework-independent state machine. Neither token is persisted or logged.
export function verificationError(code = 'unavailable') {
  const error = new Error(code)
  error.name = code === 'cancelled' ? 'AbortError' : 'HumanVerificationError'
  error.code = code
  error.humanVerification = true
  return error
}

// App service runtimes do not all provide AbortController.
export function createCancellation() {
  const listeners = new Set()
  const signal = {
    aborted: false,
    addEventListener(type, listener) { if (type === 'abort') listeners.add(listener) },
    removeEventListener(type, listener) { if (type === 'abort') listeners.delete(listener) }
  }
  return {
    signal,
    abort() {
      if (signal.aborted) return
      signal.aborted = true
      listeners.forEach(listener => listener())
      listeners.clear()
    }
  }
}

export function waitWithSignal(promise, signal) {
  if (!signal) return promise
  if (signal.aborted) return Promise.reject(verificationError('cancelled'))
  return new Promise((resolve, reject) => {
    const cleanup = () => signal.removeEventListener('abort', abort)
    const abort = () => { cleanup(); reject(verificationError('cancelled')) }
    signal.addEventListener('abort', abort)
    promise.then(value => { cleanup(); resolve(value) }, error => { cleanup(); reject(error) })
  })
}

export function parseResponseData(data) {
  if (typeof data === 'string') {
    try { return JSON.parse(data) } catch (_) { return {} }
  }
  if (data instanceof ArrayBuffer) {
    if (data.byteLength > 8192) return {}
    return parseResponseData(String.fromCharCode.apply(null, new Uint8Array(data)))
  }
  return data && typeof data === 'object' ? data : {}
}

export function createHumanVerification({ apiBase, request, challenge, now = Date.now }) {
  const base = apiBase.replace(/\/+$/, '')
  const parsed = /^(https?:\/\/[^/?#]+)(\/[^?#]*)?$/i.exec(base)
  if (!parsed || !/\/api$/.test(parsed[2] || '')) throw new Error('Expected an absolute API base ending in /api')
  const origin = parsed[1].toLowerCase()
  const root = (parsed[2] || '').slice(0, -4)
  let epoch = 0
  let clearance = null
  let disabledUntil = 0
  let flight = null

  function isProtected(url, method) {
    if (String(method || 'GET').toUpperCase() === 'OPTIONS') return false
    const target = /^(https?:\/\/[^/?#]+)(\/[^?#]*)?/i.exec(String(url))
    if (!target || target[1].toLowerCase() !== origin) return false
    const path = target[2] || '/'
    if (root && path !== root && !path.startsWith(root + '/')) return false
    const relative = path.slice(root.length)
    if (/^\/api\/security\/(config|verify)\/?$/.test(relative)) return false
    return !/^\/(static(?:\/|$)|websocket(?:\/|$)|health\/?$|docs(?:\/|$)|redoc(?:\/|$)|openapi\.json$)/.test(relative)
  }

  function clear() {
    epoch += 1
    clearance = null
    disabledUntil = 0
    const previous = flight
    flight = null
    if (previous) previous.cancel.abort()
  }

  function checkCurrent(current) {
    if (current.epoch !== epoch || current.cancel.signal.aborted) throw verificationError('cancelled')
  }

  async function run(current) {
    checkCurrent(current)
    const options = { signal: current.cancel.signal, timeout: 15000 }
    const configResponse = await request(Object.assign({}, options, { url: base + '/security/config', method: 'GET' }))
    checkCurrent(current)
    const configBody = parseResponseData(configResponse.data)
    const config = configBody.data
    if (configResponse.statusCode !== 200 || configBody.success !== true || !config || typeof config.enabled !== 'boolean') {
      throw verificationError('unavailable')
    }
    if (!config.enabled) {
      clearance = null
      disabledUntil = now() + 5000
      return { token: '', epoch }
    }
    disabledUntil = 0
    if (typeof config.siteKey !== 'string' || !config.siteKey || config.action !== 'api_access') throw verificationError('unavailable')
    const token = await challenge(config, current.cancel.signal)
    checkCurrent(current)
    if (typeof token !== 'string' || !token.trim() || token.length > 2048) throw verificationError('invalid')
    // A Cloudflare token is submitted exactly once. Retrying requires a fresh challenge.
    const verified = await request(Object.assign({}, options, {
      url: base + '/security/verify', method: 'POST', header: { 'X-Turnstile-Token': token }
    }))
    checkCurrent(current)
    const body = parseResponseData(verified.data)
    if (verified.statusCode !== 200 || body.success !== true) {
      throw verificationError(verified.statusCode === 403 ? 'invalid' : 'unavailable')
    }
    const data = body.data || {}
    const ttl = Number(data.expiresIn)
    const expiresAt = Number(data.expiresAt) * 1000
    const expiry = Math.min(now() + ttl * 1000, expiresAt)
    if (typeof data.verificationToken !== 'string' || !data.verificationToken || !Number.isFinite(ttl) || ttl <= 0 || !Number.isFinite(expiry) || expiry <= now()) {
      throw verificationError('unavailable')
    }
    clearance = { token: data.verificationToken, expiresAt: expiry - Math.min(5000, ttl * 100), epoch }
    return { token: clearance.token, epoch }
  }

  function ensure(signal) {
    if (signal && signal.aborted) return Promise.reject(verificationError('cancelled'))
    if (clearance && clearance.expiresAt > now()) return Promise.resolve({ token: clearance.token, epoch })
    if (disabledUntil > now()) return Promise.resolve({ token: '', epoch })
    if (!flight) {
      const current = { epoch, cancel: createCancellation(), promise: null, waiters: 0 }
      flight = current
      current.promise = waitWithSignal(Promise.resolve().then(() => run(current)), current.cancel.signal)
      const cleanup = () => { if (flight === current) flight = null }
      current.promise.then(cleanup, cleanup)
    }
    const current = flight
    current.waiters += 1
    const release = () => {
      current.waiters -= 1
      // Give React StrictMode/remounts a microtask to attach their replacement waiters.
      queueMicrotask(() => {
        if (flight === current && current.waiters === 0) clear()
      })
    }
    return waitWithSignal(current.promise, signal).then(
      value => { release(); return value },
      error => { release(); throw error }
    )
  }

  function inspectResponse(status, data, sent) {
    const body = parseResponseData(data)
    const code = String(body.statusCode || '')
    if (status === 403 && (code === '40310' || code === '40311')) {
      // A late response from an old session/token cannot invalidate a new credential.
      if (sent && sent.epoch === epoch && sent.token === (clearance ? clearance.token : '')) clear()
      return verificationError('invalid')
    }
    if (status === 503) return verificationError('unavailable')
    return null
  }

  return { ensure, clear, isProtected, inspectResponse, getExpiresAt: () => clearance ? clearance.expiresAt : 0, isCurrent: sent => !!sent && sent.epoch === epoch && sent.token === (clearance ? clearance.token : '') }
}
